/**
 * Rule Detail Invariants
 *
 * Enforces:
 * 1. Permissions - source-based command access control
 *
 * Permission matrix:
 * - user: save/submit, save/cancel
 * - api: data/*, loading/*
 * - ai: nothing
 * - url: nothing
 *
 * No normalize needed (no computed fields to clamp).
 * No validate needed (no form validation — editing is handled by RuleConfigurationForm).
 */

import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { RuleDetailState } from './state';
import { RuleDetailCommand } from './commands';

/**
 * Command operations — `save/submit` flips `loading.saving`, which the
 * `saveRulePolicy` reacts to by calling `SetRule`. Capability is DERIVED from
 * the named client method. `save/cancel` is not a mutation → ungated.
 */
export const commandOperations: CommandOperationMap<RuleDetailState, RuleDetailCommand> = {
  'automation/ruleDetail/save/submit': 'SetRule'
};

/**
 * Permission control - source-based command access.
 */
const canApply = ({
  state,
  command,
  meta
}: {
  state: RuleDetailState;
  command: RuleDetailCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // Application source: full access for internal coordination
  if ( source === 'application' ) {
    return true;
  }

  // Policy source: full access for internal coordination
  if ( source === 'policy' ) {
    return true;
  }

  // User source: can only trigger save actions; the mutating `save/submit` is
  // gated by the backend operation declared in commandOperations.
  if ( source === 'user' ) {
    if ( type === 'automation/ruleDetail/save/cancel' ) return true;
    if ( type === 'automation/ruleDetail/save/submit' ) {
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }
    return false;
  }

  // API source: data and loading commands
  if ( source === 'api' ) {
    return (
      type.startsWith( 'automation/ruleDetail/data/' ) ||
      type.startsWith( 'automation/ruleDetail/loading/' )
    );
  }

  // AI source: nothing allowed
  if ( source === 'ai' ) {
    return false;
  }

  // URL source: nothing allowed
  if ( source === 'url' ) {
    return false;
  }

  // Unknown sources: deny
  return false;
};

export const invariants: Invariants<RuleDetailState, RuleDetailCommand> = {
  canApply
};
